import registry from "./checklistTransitions.json";

/**
 * The categories a task template is split into: ONE PER STEP of a status flow
 * (owner change, Oct 6 2026). Every job type, visit type and PO type owns its own
 * ordered status list (today's flow is the default), and a task belongs to the
 * transition it helps drive and GATES it — the job/visit/PO can't move past that
 * status while any task there is open.
 *
 * ⚠️ The data is not written here: `checklistTransitions.json` is GENERATED from
 * `backend_v2/domain/checklists/transitions.py` (default flows, section blurbs,
 * the auto-tracked requirement templates, default show rules), and a backend test
 * fails when it goes stale. Regenerate with
 *   backend_v2/.venv/bin/python -m domain.checklists.transitions
 * This file only reads it, builds the categories of ANY flow (`buildTransitions`)
 * and adds the display helpers. The backend's `build_transitions` does the same
 * thing in Python — keep them in step.
 *
 * A task stores its transition as `from_status` (the status it waits to leave).
 * `scope` still carries entity + audience and keys the API payload buckets — a
 * job transition's two scopes are the task's AUDIENCE (PMO tasks are hidden from
 * technicians). Which bucket a transition's tasks live in depends on its `from`
 * KEY alone, so reordering a type's statuses never strands a task.
 */

export const ENTITY_JOB = "job";
export const ENTITY_VISIT = "visit";
export const ENTITY_PO = "po";

export const SHOW_MODE_STATUS = "status";
export const SHOW_MODE_DAYS_BEFORE = "days_before";
/** Portal-only, jobs only: shows once the job's LAST visit day has arrived (what job closure tasks always did). */
export const SHOW_MODE_LAST_VISIT_DAY = "last_visit_day";

/** What a day-count rule counts back from, per entity. */
export const SHOW_ANCHORS = {
  [ENTITY_JOB]: "first workday",
  [ENTITY_VISIT]: "visit",
  [ENTITY_PO]: "start date",
};

export const MAX_SHOW_DAYS_BEFORE = 365;

export const statusLabel = (s) =>
  s ? s.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") : "";

/** The DEFAULT flow of each entity, as [{ key, label }]. */
export const DEFAULT_FLOWS = registry.flows;
/** Statuses outside every flow — fixed, not editable. */
export const OFF_FLOW = registry.offFlow;

/** Whether a status is one of the entity's built-in ones (vs added by a type). */
export const isBuiltin = (entity, key) => DEFAULT_FLOWS[entity].some((f) => f.key === key);

/**
 * A status's "Auto" toggle: may a record be moved INTO it automatically? The
 * defaults come from the backend (`DEFAULT_AUTO` in transitions.py) — planning,
 * scheduled and every PO status are manual stops, the statuses the app drives
 * itself are automatic, one a type adds is automatic. The last status of a job
 * or visit (and a job/visit's first) is always a person's move.
 */
export const autoEditable = (entity, key) => !(registry.neverAuto[entity] || []).includes(key);
export const defaultAuto = (entity, key) => {
  if (!autoEditable(entity, key)) return false;
  const table = registry.defaultAuto[entity] || {};
  return key in table ? table[key] : true;
};

/**
 * A type's flow as [{ key, label }]. `statuses` is the type's `statuses` field
 * (already the effective list when it came from the API); null/empty — or no
 * type at all — is the default flow.
 */
export function flowFor(entity, statuses) {
  return Array.isArray(statuses) && statuses.length >= 2
    ? statuses.map((s) => ({
        key: s.key,
        label: s.label || statusLabel(s.key),
        auto: autoEditable(entity, s.key) && (typeof s.auto === "boolean" ? s.auto : defaultAuto(entity, s.key)),
      }))
    : DEFAULT_FLOWS[entity].map((f) => ({ ...f, auto: defaultAuto(entity, f.key) }));
}

export const flowKeys = (flow) => flow.map((f) => f.key);

/** A status's label in a flow; off-flow and unknown keys fall back to a humanized key. */
export function labelIn(flow, key) {
  return flow?.find((f) => f.key === key)?.label ?? statusLabel(key);
}

/** Every status a record may carry, as dropdown options: the flow, then the fixed off-flow ones. */
export function statusOptions(entity, flow) {
  return [
    ...flowFor(entity, flow).map((f) => ({ value: f.key, label: f.label })),
    ...OFF_FLOW[entity].map((k) => ({ value: k, label: statusLabel(k) })),
  ];
}

/** Which scope bucket(s) a transition starting from `from` stores its tasks in. */
export function scopesForFrom(entity, from) {
  const s = registry.scopes[entity];
  return s.lateFrom.includes(from) ? s.late : s.early;
}

const fill = (line, fromLabel, toLabel) => line.replace(/\{from\}/g, fromLabel).replace(/\{to\}/g, toLabel);

/**
 * One category per step of `flow` ([{ key, label }], default: the entity's
 * default flow): { key, entity, from, to, fromLabel, toLabel, scopes, blurb,
 * requirements, defaultShow }.
 */
export function buildTransitions(entity, flow = DEFAULT_FLOWS[entity]) {
  const builtin = DEFAULT_FLOWS[entity].map((f) => f.key);
  return flow.slice(0, -1).map((a, i) => {
    const b = flow[i + 1];
    const lines =
      (builtin.includes(b.key) && registry.requirementsByTo[entity][b.key]) ||
      (entity === ENTITY_PO && builtin.includes(b.key) ? registry.poDefaultRequirements : registry.customRequirements);
    return {
      key: `${entity}_${a.key}_${b.key}`,
      entity,
      from: a.key,
      to: b.key,
      fromLabel: a.label,
      toLabel: b.label,
      scopes: scopesForFrom(entity, a.key),
      blurb: registry.blurbs[entity][a.key] || registry.defaultBlurb[entity],
      requirements: lines.map((l) => fill(l, a.label, b.label)),
      defaultShow: registry.defaultShow[entity][a.key] || null,
    };
  });
}

export const transitionTitle = (t) => (t.isOther ? "Other" : `${t.fromLabel} → ${t.toLabel}`);

/** Which entity a scope bucket belongs to. */
export function entityForScope(scope) {
  return Object.keys(registry.scopes).find((e) => {
    const s = registry.scopes[e];
    return s.early.includes(scope) || s.late.includes(scope);
  });
}

/** The transition a task belonged to before tasks stored one — the fallback for a row that carries none. */
const LEGACY_FROM = {
  pmo_planning: "draft",
  tech_planning: "draft",
  pmo_closure: "pending_closure",
  tech_closure: "pending_closure",
  visit: "in_progress",
  visit_closure: "in_review",
  po: "fulfilled",
};

/** A row's transition: its own `from_status`, else the legacy one for its scope. */
export const fromStatusOf = (row, fallbackScope) =>
  row?.from_status || LEGACY_FROM[row?.scope || fallbackScope];

/**
 * The transitions an editor given these scope buckets shows, for the type's
 * `flow`: every step of the flow of the entity those scopes belong to.
 */
export function transitionsForScopes(scopes, flow) {
  const entity = entityForScope(scopes[0]);
  return buildTransitions(entity, flow || DEFAULT_FLOWS[entity]);
}

/**
 * The statuses a task's "when status is" rule may name: the task's OWN step and
 * every status BEFORE it in the flow, never a later one — a task that holds the
 * record back from moving past its step must not stay hidden until after that
 * move (owner rule, Oct 7 2026; the server refuses a later one too). `flow` is
 * [{ key, label }]; a step that isn't in the flow (a status the type dropped)
 * leaves the whole flow on offer rather than an empty list.
 */
export function showStatusOptions(flow, fromStatus) {
  const at = flow.findIndex((f) => f.key === fromStatus);
  return at === -1 ? flow : flow.slice(0, at + 1);
}

/** `show_*` fields for a NEW task in this transition. */
export function defaultShowFields(entity, from) {
  const rule = registry.defaultShow[entity][from];
  if (!rule) return { show_mode: null, show_status: null, show_days_before: null };
  if (rule.mode === SHOW_MODE_STATUS) {
    return { show_mode: SHOW_MODE_STATUS, show_status: rule.status, show_days_before: null };
  }
  return { show_mode: SHOW_MODE_DAYS_BEFORE, show_status: null, show_days_before: rule.days };
}

/** One-line description of a task's show rule, e.g. "Shows on My Tasks once the job is Scheduled". */
export function describeShow(item, flow) {
  const entity = entityForScope(item?.scope);
  if (item?.show_mode === SHOW_MODE_STATUS && item.show_status) {
    const noun = entity === ENTITY_JOB ? "the job" : entity === ENTITY_VISIT ? "the visit" : "the PO";
    return `Shows on My Tasks once ${noun} is ${labelIn(flow, item.show_status)}`;
  }
  if (item?.show_mode === SHOW_MODE_LAST_VISIT_DAY) {
    return "Shows on My Tasks once the job's last work visit day arrives";
  }
  if (item?.show_mode === SHOW_MODE_DAYS_BEFORE && item.show_days_before != null) {
    return `Shows on My Tasks ${item.show_days_before} day${item.show_days_before === 1 ? "" : "s"} before the ${SHOW_ANCHORS[entity] || "start"}`;
  }
  return null;
}

/**
 * Split rows into [{ t, items }] by the transition they belong to, in flow
 * order, skipping transitions with no rows. The live checklists use this so a
 * job/visit/PO shows only the categories it actually has tasks in. Rows whose
 * transition isn't in the flow any more (a status the type dropped) are kept
 * under a final "Other" group rather than disappearing.
 */
export function groupByTransition(entity, rows = [], flow) {
  const transitions = buildTransitions(entity, flow || DEFAULT_FLOWS[entity]);
  const groups = transitions
    .map((t) => ({ t, items: rows.filter((r) => fromStatusOf(r) === t.from) }))
    .filter(({ items }) => items.length > 0);
  const known = new Set(transitions.map((t) => t.from));
  const orphans = rows.filter((r) => !known.has(fromStatusOf(r)));
  if (orphans.length > 0) {
    groups.push({
      t: {
        key: `${entity}_other`,
        entity,
        from: null,
        to: null,
        fromLabel: "Other",
        toLabel: "",
        scopes: [],
        blurb: "",
        requirements: [],
        defaultShow: null,
        isOther: true,
      },
      items: orphans,
    });
  }
  return groups;
}

/**
 * "2 Draft → Scheduled · 1 Pending Closure → Pending Approval" — a one-line
 * summary of a template's size by category. `countFor(from)` gives a transition's
 * task count.
 */
export function summarizeByTransition(entity, countFor, flow) {
  return buildTransitions(entity, flow || DEFAULT_FLOWS[entity])
    .map((t) => ({ n: countFor(t.from) || 0, label: transitionTitle(t) }))
    .filter(({ n }) => n > 0)
    .map(({ n, label }) => `${n} ${label}`)
    .join(" · ");
}
